import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import apiClient from '../../services/apiClient';
import { buildApiUrl } from '../../utils/apiBaseUrl';
import { WizardComponentProps } from '../../types';

type EntityItem = {
  Step?: number;
  Object?: string;
  Entity: string;
  Required?: boolean;
  selected?: boolean;
  mandatoryFields?: string[];
  defaultOptions?: Record<string, string[]>;
  requiresEntities?: string[];
};

const PhaseEntities: React.FC<WizardComponentProps> = ({ isEmbedded }) => {
  const { customerId, phaseId } = useParams<{ customerId: string; phaseId: string }>();
  const queryClient = useQueryClient();
  const [catalog, setCatalog] = useState<EntityItem[]>([]);
  const [selected, setSelected] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const load = async () => {
      try {
        let res = await fetch(buildApiUrl('/entities.json'), { cache: 'no-store' });
        if (!res.ok) res = await fetch('/entities.json', { cache: 'no-store' });
        const data = await res.json();
        setCatalog(data);
      } catch { }
    };
    load();
  }, []);

  const { data: selectedServer, isSuccess: isServerDataLoaded } = useQuery({
    queryKey: ['phase-entities', customerId, phaseId],
    queryFn: () => apiClient.getPhaseEntities(customerId!, phaseId!),
    enabled: !!(customerId && phaseId)
  });

  const merged = useMemo<EntityItem[]>(() => {
    // Merge catalog with selected entities from server
    const selectedMap: Record<string, EntityItem> = {};
    if (Array.isArray(selectedServer)) {
      for (const it of selectedServer as EntityItem[]) {
        selectedMap[it.Entity] = it;
      }
    }

    // Show all catalog entities with their selection status
    const list: EntityItem[] = [];
    const catalogEntityNames = new Set(catalog.map(c => c.Entity));

    for (const c of catalog) {
      const sel = selectedMap[c.Entity];
      list.push({
        Step: c.Step,
        Object: c.Object,
        Entity: c.Entity,
        Required: c.Required,
        selected: sel?.selected !== undefined ? !!sel.selected : false, // Default to false, let user choose
        mandatoryFields: c.mandatoryFields,
        defaultOptions: c.defaultOptions,
        requiresEntities: c.requiresEntities
      });
    }

    // Include dynamically discovered entities from the server that are not in the standard catalog
    if (Array.isArray(selectedServer)) {
      for (const sel of selectedServer as EntityItem[]) {
        if (!catalogEntityNames.has(sel.Entity)) {
          list.push({
            Step: 999, // Push to the end
            Object: sel.Object || sel.Entity,
            Entity: sel.Entity,
            Required: false,
            selected: !!sel.selected,
            mandatoryFields: sel.mandatoryFields,
            defaultOptions: sel.defaultOptions
          });
        }
      }
    }

    // Sort by step number for better UX, then alphabetically for those without step
    return list.sort((a, b) => {
      const stepA = a.Step || 999;
      const stepB = b.Step || 999;
      if (stepA !== stepB) return stepA - stepB;
      return a.Entity.localeCompare(b.Entity);
    });
  }, [catalog, selectedServer]);

  // Build a reverse dependency map: entity -> list of entities that depend on it
  const requiredByMap = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const item of merged) {
      if (item.requiresEntities) {
        for (const dep of item.requiresEntities) {
          if (!map[dep]) map[dep] = [];
          map[dep].push(item.Entity);
        }
      }
    }
    return map;
  }, [merged]);

  // Check if an entity is locked because a dependent entity is selected
  const isLockedByDependency = (entityName: string): string | null => {
    const dependents = requiredByMap[entityName];
    if (!dependents) return null;
    const activeDep = dependents.find(dep => selected[dep]);
    return activeDep || null;
  };

  useEffect(() => {
    if (!isServerDataLoaded || merged.length === 0) return;

    setSelected(prev => {
      // If prev is completely empty, it's the initial load.
      if (Object.keys(prev).length === 0) {
        const def: Record<string, boolean> = {};
        for (const m of merged) def[m.Entity] = !!m.selected;
        return def;
      }

      // Otherwise, only preserve local UI changes and add any newly discovered entities
      const next = { ...prev };
      let changed = false;
      for (const m of merged) {
        if (next[m.Entity] === undefined) {
          next[m.Entity] = !!m.selected;
          changed = true;
        } else if (m.Required && !next[m.Entity]) {
          // Enforce required entities
          next[m.Entity] = true;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [merged]);

  const saveMutation = useMutation({
    mutationFn: (entities: any[]) => apiClient.updatePhaseEntities(customerId!, phaseId!, entities),
    onSuccess: () => {
      toast.success('Entities updated');
      queryClient.invalidateQueries({ queryKey: ['phase-entities', customerId, phaseId] });
    },
    onError: () => toast.error('Failed to update entities')
  });

  const handleSave = () => {
    // Only include entities that are selected by the user
    const payload: EntityItem[] = merged
      .filter(e => selected[e.Entity]) // Only selected entities
      .map((e) => ({
        Step: e.Step,
        Object: e.Object,
        Entity: e.Entity,
        Required: e.Required,
        selected: true, // All entities in payload are selected
        mandatoryFields: e.mandatoryFields,
        defaultOptions: e.defaultOptions
      }));

    console.log('Saving selected entities:', payload.map(e => e.Entity));
    saveMutation.mutate(payload as any);
  };

  return (
    <div className="max-w-4xl mx-auto">
      {!isEmbedded && (
        <>
          {/* Breadcrumb Navigation */}
          <nav className="mb-4 flex items-center text-sm text-gray-600">
            <Link to="/customers" className="hover:text-primary-600 transition-colors">
              Customers
            </Link>
            <span className="mx-2">/</span>
            <Link
              to={`/customers/${customerId}/phases`}
              className="hover:text-primary-600 transition-colors"
            >
              Phases
            </Link>
            <span className="mx-2">/</span>
            <Link
              to={`/customers/${customerId}/phases/${phaseId}`}
              className="hover:text-primary-600 transition-colors"
            >
              Phase Dashboard
            </Link>
            <span className="mx-2">/</span>
            <span className="text-gray-900 font-medium">Entities</span>
          </nav>

          {/* Back Button */}
          <Link
            to={`/customers/${customerId}/phases/${phaseId}`}
            className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 font-medium mb-4 transition-colors"
          >
            <ChevronLeftIcon className="w-4 h-4 mr-1" />
            Back to Phase Dashboard
          </Link>

          <h1 className="text-2xl font-bold mb-4">Entities for this phase</h1>
        </>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 p-3 border rounded max-h-[60vh] overflow-auto">
        {merged.filter(item => !item.Entity.match(/^PaymentInformationDetailV3[A-Z]{3}$/)).map((item) => (
          <label key={item.Entity} className="flex items-center space-x-2 text-sm">
            <input
              type="checkbox"
              checked={!!selected[item.Entity]}
              disabled={!!item.Required}
              onChange={(e) => {
                const checked = e.target.checked;

                // Block unchecking if another selected entity requires this one
                if (!checked) {
                  const lockedBy = isLockedByDependency(item.Entity);
                  if (lockedBy) {
                    toast.error(`Cannot deselect ${item.Entity} — it is required by ${lockedBy}. Deselect ${lockedBy} first.`);
                    return;
                  }
                }

                setSelected((prev) => {
                  const next = { ...prev, [item.Entity]: checked };

                  // Auto-select dependencies when checking an entity
                  if (checked && item.requiresEntities) {
                    for (const dep of item.requiresEntities) {
                      if (!next[dep]) {
                        next[dep] = true;
                        toast(`Auto-selected ${dep} (required by ${item.Entity})`, { icon: '🔗' });
                      }
                    }
                  }

                  // Cascade uncheck: If PaymentInformationDetailV3 is unchecked, uncheck all country specifics
                  if (!checked && item.Entity === 'PaymentInformationDetailV3') {
                    Object.keys(next).forEach(key => {
                      if (key.match(/^PaymentInformationDetailV3[A-Z]{3}$/)) {
                        next[key] = false;
                      }
                    });
                  }

                  return next;
                });

                // If PaymentInformationDetailV3 is being checked, instantly discover related dynamic entities
                if (checked && item.Entity === 'PaymentInformationDetailV3' && customerId && phaseId) {
                  toast.promise(
                    apiClient.discoverDynamicEntities(customerId, phaseId, 'PaymentInformationDetailV3'),
                    {
                      loading: 'Discovering country-specific Payment Info extensions...',
                      success: (data) => {
                        if (data.newDynamicEntities?.length > 0) {
                          queryClient.invalidateQueries({ queryKey: ['phase-entities', customerId, phaseId] });
                          return `Discovered ${data.newDynamicEntities.length} new extensions!`;
                        }
                        return 'Ready for mapping. (No new extensions found)';
                      },
                      error: 'Failed to discover dynamic entities'
                    }
                  );
                }
              }}
            />
            <span>
              <span className="font-medium">{item.Step ? `${item.Step}. ` : ''}{item.Entity}</span>
              {item.Object && <span className="ml-2 text-gray-500">({item.Object})</span>}
              {item.Required && <span className="ml-2 text-red-600">required</span>}
              {isLockedByDependency(item.Entity) && <span className="ml-2 text-amber-600" title={`Required by ${isLockedByDependency(item.Entity)}`}>🔒</span>}
              {item.requiresEntities && <span className="ml-2 text-xs text-blue-500">requires {item.requiresEntities.join(', ')}</span>}
            </span>
          </label>
        ))}
      </div>

      {merged.some(item => item.Entity.match(/^PaymentInformationDetailV3[A-Z]{3}$/)) && (
        <div className="mt-6">
          <h2 className="text-lg font-semibold mb-3 text-gray-800 border-b pb-2 flex items-center">
            <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full mr-2">Extensions</span>
            Payment Information Country Specifics
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 p-3 bg-gray-50 border border-gray-200 rounded max-h-[40vh] overflow-auto">
            {merged.filter(item => item.Entity.match(/^PaymentInformationDetailV3[A-Z]{3}$/)).map((item) => {
              const countryCode = item.Entity.replace('PaymentInformationDetailV3', '');
              return (
                <label key={item.Entity} className="flex items-center space-x-2 text-sm bg-white p-2 border rounded shadow-sm hover:border-blue-300 transition-colors cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!selected[item.Entity]}
                    disabled={!!item.Required}
                    className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                    onChange={(e) => {
                      setSelected((prev) => ({ ...prev, [item.Entity]: e.target.checked }));
                    }}
                  />
                  <span className="flex flex-col">
                    <span className="font-medium text-gray-900">{countryCode} </span>
                    <span className="text-xs text-gray-500 truncate" title={item.Entity}>{item.Entity}</span>
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-6 flex justify-end">
        <button onClick={handleSave} className="btn-primary" disabled={saveMutation.isPending}>Save Entities Selection</button>
      </div>
    </div>
  );
};

export default PhaseEntities;


